import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { styled } from "@mui/material/styles";
import { IconButton } from "@mui/material";
import { FieldHeader, TextInput } from "../../../components/General";
import { colors } from "../../../styles";
import useGetOnboarding from "../../../hooks/queries/admin/useGetOnboarding";
import { StepComponentProps } from "./index";
import useSaveOnboarding from "../../../hooks/mutations/admin/useSaveOnboarding";
import api from "../../../api/api";
import closeIcon from "../../../assets/icons/close-icon.svg";
import upload from "../../../assets/icons/greenIcons/upload.svg";
import Button from "../../../components/Button";
import { QUERY_ONBOARDING_KEY } from "../../../api/queryKeys";

const Container = styled("div")(`
  width: 100%;
`);

const InnerContainer = styled("div")({
  width: "55%",
  margin: "auto",
});

const FormatDetails = styled("p")(`
  margin: 6px auto;
  width: fit-content;
  font-weight: 400;
  font-size: 14px;
  text-align: center;
  color: #475467;
  cursor: pointer;
`);

const DropBox = styled("div")(`
  height: 160px;
  border: 1px dashed ${colors.gray};
  border-radius: 8px;
  background: ${colors.white};
  margin: 8px auto;
`);

const PageTitle = styled("h2")(`
  font-family: Inter;
  font-weight: 600;
  font-size: 20px;
  text-align: center;
  margin: 70px auto 0px;
`);

const PageSubTitle = styled("p")(`
  font-family: Inter;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
  color: #475467;
  width: 60%;
  margin: 10px auto;
`);

const UploadHeader = styled("h2")(`
  font-family: Inter;
  font-style: normal;
  font-weight: 500;
  display: flex;
  justify-content: center;
  font-size: 14px;
  line-height: 17px;
  color: ${colors.black};
  margin: 0;
`);

const UploadImg = styled("img")(`
  width: 50px;
  display: flex;
  margin: 30px auto 10px;
`);

const FileInfoRow = styled("div")(`
  display: flex;
  width: 100%;
  margin: 10px 0 0;
  justifyContent: space-between;
  alignItems: center;
  fontFamily: Inter;
  fontSize: 14px;
  color: #758790;
`);

const FileInfo = styled("div")(`
  display: flex;
  flexDirection: row;
  justifyContent: space-between;
  width: 90px;
  alignItems: center;
`);

const pageCopy = {
  title: `Welcome to TIFIN @Work’s Emergency Savings solution.`,
  subTitle: `You’re a few steps away from helping your employees save for the future. You can launch this program at any time of year.`,
  description: `Add essential company information.`,
};

const defaultFormValues = {
  name: "",
  total_number_of_employees: "",
  phone_number: "",
  payroll_system: "",
};

const CompanyProfile = ({ nextStep }: StepComponentProps) => {
  // state
  const [values, setValues] = useState(defaultFormValues);
  const [emphasis, setEmphasis] = useState<any>(null);
  const [logo, setLogo] = useState<File | null>(null);
  const [fileError, setFileError] = useState<any>(null);

  // Queries
  const { data: initialData, error } = useGetOnboarding();
  const { mutateAsync: saveOnboarding } = useSaveOnboarding({
    onSuccessHandler: nextStep,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (initialData) {
      const employerData = initialData?.employer;
      if (employerData) {
        setValues({
          name: employerData?.name,
          total_number_of_employees: employerData?.total_number_of_employees,
          phone_number: employerData?.phone_number,
          payroll_system: employerData?.payroll_system,
        });
      }
    }
  }, [initialData]);

  const setValue = (field: string, value: any) =>
    setValues((old) => ({ ...old, [field]: value }));

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (
      !values.name ||
      !values.total_number_of_employees ||
      !values.phone_number ||
      !values.payroll_system ||
      (!logo && !initialData?.employer?.logo)
    ) {
      toast.error("Please fill out all fields");
      return;
    }
    let form_data = new FormData();
    if (logo) {
      // we need special handling for the logo
      // expects a form data object
      form_data.append("logo", logo, logo.name);
      try {
        await api.put(QUERY_ONBOARDING_KEY, form_data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (error) {
        toast.error("Error uploading logo");
      }
    }
    await saveOnboarding({ employerItems: { ...values } });
  };

  const allowDrop = (event: any) => {
    setEmphasis(!emphasis);
    event.preventDefault();
  };

  const drop = (event: any) => {
    event.preventDefault();
    const fileData = event.dataTransfer.files[0];
    setLogo(fileData);
    setEmphasis(false);
  };

  if (initialData) {
    const hasUploadedLogo = initialData?.employer?.logo;
    return (
      <Container>
        <PageTitle>{pageCopy.title}</PageTitle>
        <PageSubTitle>{pageCopy.subTitle}</PageSubTitle>
        <PageTitle sx={{ margin: "30px auto 0" }}>
          {pageCopy.description}
        </PageTitle>
        <InnerContainer>
          <form onSubmit={handleSubmit}>
            <FieldHeader sx={{ margin: "20px 0 0" }}>Company Name</FieldHeader>
            <TextInput
              size="small"
              name="name"
              placeholder="Company name"
              value={values.name}
              onChange={(e) => setValue("name", e.target.value)}
              required
            />
            <FieldHeader sx={{ margin: "10px 0 0" }}>
              # of Employees
            </FieldHeader>
            <TextInput
              size="small"
              name="total_number_of_employees"
              placeholder="# of Employees"
              value={values.total_number_of_employees}
              type="number"
              onChange={(e) =>
                setValue("total_number_of_employees", e.target.value)
              }
              required
            />
            <FieldHeader sx={{ margin: "10px 0 0" }}>
              Company Phone Number
            </FieldHeader>
            <TextInput
              size="small"
              placeholder="Phone Number"
              name="phone_number"
              value={values.phone_number}
              onChange={(e) => setValue("phone_number", e.target.value)}
              required
            />
            <FieldHeader sx={{ margin: "10px 0 0" }}>
              Payroll System
            </FieldHeader>
            <TextInput
              size="small"
              name="payroll_system"
              placeholder="Payroll system"
              value={values.payroll_system}
              onChange={(e) => setValue("payroll_system", e.target.value)}
            />
            <FieldHeader sx={{ margin: "30px 0 10px" }}>
              Company Logo
            </FieldHeader>
            {hasUploadedLogo && !logo ? (
              <img src={hasUploadedLogo} alt="logo" />
            ) : null}
            <DropBox onDrop={drop} onDragOver={allowDrop}>
              <UploadImg src={upload} alt="upload" />
              <UploadHeader>
                <label
                  style={{ cursor: "pointer" }}
                  htmlFor="upload-file-button"
                >
                  Click to upload
                </label>
                <input
                  name="logo"
                  type="file"
                  id="upload-file-button"
                  accept=".png, .jpg, .jpeg"
                  hidden
                  onChange={(e) => {
                    setLogo(e.target?.files ? e.target?.files[0] : null);
                  }}
                />
                &nbsp;{" "}
                <p style={{ margin: "0", fontWeight: 400 }}>or drag and drop</p>
              </UploadHeader>
              <FormatDetails>
                Accepted file types include .png, .jpg, .jpeg
              </FormatDetails>
              {logo && (
                <FileInfoRow>
                  <p>{logo.name}</p>
                  <FileInfo>
                    <IconButton
                      onClick={() => {
                        setFileError(null);
                        setLogo(null);
                      }}
                    >
                      <img
                        style={{ marginTop: 4, width: 10 }}
                        src={closeIcon}
                        alt="remove logo"
                      />
                    </IconButton>
                  </FileInfo>
                </FileInfoRow>
              )}
            </DropBox>

            <Button type="submit">Continue</Button>
          </form>
        </InnerContainer>
      </Container>
    );
  }
};

export default CompanyProfile;
